import { Button } from '@readcloud/component-library/components/Base/Button';
import {
  Input,
  InputGroupText,
} from '@readcloud/component-library/components/Base/Form';
import { InputGroup, InputGroupAppend } from '@readcloud/ui';
import { LoadingIcon } from '@readcloud/component-library/components/Base/LoadingIcon';
import { Tooltip, TooltipTrigger, TooltipContent } from '@readcloud/ui';
import C from 'classnames';
import React from 'react';

import { findStates } from '../../../types/SearchBarConstants';

import {
  searchActions,
  searchSelectors,
  useAppDispatch,
} from '@readcloud/state';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover, PopoverContent, PopoverTrigger } from '@readcloud/ui';
import {
  MdChevronLeft,
  MdChevronRight,
  MdClose,
  MdExpandLess,
  MdMoreVert,
} from 'react-icons/md';
import { useSelector } from 'react-redux';
import { DocumentWithExtrasContext } from '../../../context/DocumentWithExtras';
import { Switch } from '@readcloud/ui';

const renderMoreButton = ({
  onClick,
  ref,
}: {
  onClick: () => void;
  ref: React.Ref<HTMLButtonElement>;
}) => (
  <Button ref={ref} onClick={onClick}>
    <small className="fe fe-more-vert"></small>
  </Button>
);
export const SearchBar = (props) => {
  const [justOpened, setJustOpened] = React.useState<boolean>(true);
  const searchData = useSelector(searchSelectors.getSearchData);
  const dispatch = useAppDispatch();

  const { extras, findController, type } = React.useContext(
    DocumentWithExtrasContext
  );

  const toggleCaseSensitive = () => {
    dispatch(searchActions.toggleCaseSensitive());
  };
  const toggleHighlightAll = () => {
    dispatch(searchActions.toggleHighlightAll());
  };
  const togglePhraseSearch = () => {
    dispatch(searchActions.togglePhraseSearch());
  };

  const onPrevious = React.useCallback(() => {
    findController?.findPrevious();
  }, [findController]);

  const onNext = React.useCallback(() => {
    findController?.findNext();
  }, [findController]);

  let timeoutHandle = React.useRef<NodeJS.Timeout | undefined>(undefined);
  React.useEffect(() => {
    clearTimeout(timeoutHandle.current);
    if (!justOpened) {
      timeoutHandle.current = setTimeout(() => {
        findController?.find(searchData);
      }, 300);
    }
    setJustOpened(false);
  }, [searchData?.query, searchData?.caseSensitive, searchData?.highlightAll]);

  React.useEffect(() => {
    findController &&
      dispatch(searchActions.setFindState(findController?.findState));
  }, [findController?.findState]);

  React.useEffect(() => {
    findController &&
      dispatch(searchActions.setMatchCount(findController?.totalMatchCount));
  }, [findController?.totalMatchCount]);
  React.useEffect(() => {
    findController &&
      dispatch(
        searchActions.setCurrentMatchCount(findController?.currentMatchCount)
      );
  }, [findController?.currentMatchCount]);

  return (
    <div
      data-testid="divSearchBar"
      className={C('rc-flex rc-py-2 rc-items-center rc-w-full')}
    >
      <InputGroup className="rc-w-full">
        <Input
          placeholder="Search"
          name="query"
          defaultValue={searchData.query}
          onChange={(e) =>
            dispatch(searchActions.setSearchQuery(e.currentTarget.value))
          }
        />
        <InputGroupAppend className="rc-w-auto">
          {searchData.error ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  icon
                  variant="light"
                  onClick={() => {
                    findController && findController.reset();
                    dispatch(searchActions.setCloseAndClear());
                  }}
                >
                  <MdClose size="28" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Close Search</TooltipContent>
            </Tooltip>
          ) : searchData.findState === findStates.PENDING ? (
            <InputGroupText className="rc-flex rc-px-2 rc-h-full rc-items-center">
              <LoadingIcon
                loading={searchData.findState === findStates.PENDING}
                size="xs"
              />
            </InputGroupText>
          ) : searchData.findState === findStates.NOT_FOUND ? (
            <InputGroupText className="rc-flex rc-px-2 rc-h-full rc-items-center">
              0
            </InputGroupText>
          ) : (
            <>
              <InputGroupText
                className="rc-flex rc-px-2 rc-h-full rc-items-center"
                data-testid={
                  searchData.currentMatchCount > 0 && searchData.matchCount > 0
                    ? 'spanSearchDataFound'
                    : null
                }
              >
                {searchData.currentMatchCount || ''}
                {(searchData.matchCount && '/' + searchData.matchCount) || ''}
              </InputGroupText>
            </>
          )}
        </InputGroupAppend>
      </InputGroup>
      <div className="rc-px-1" />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button onClick={onPrevious} variant="light" icon>
            <MdChevronLeft size={24} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Previous Match</TooltipContent>
      </Tooltip>
      <div className="rc-px-1" />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button testId="btnSearchNext" onClick={onNext} variant="light" icon>
            <MdChevronRight size={24} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Next Match</TooltipContent>
      </Tooltip>
      <div className="rc-px-1" />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            onClick={() => dispatch(searchActions.setOpen(false))}
            variant="light"
            icon
          >
            <MdExpandLess size={24} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Hide Search Bar</TooltipContent>
      </Tooltip>
      <div className="rc-px-1" />

      <Popover>
        <PopoverTrigger>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="light" icon>
                <MdMoreVert size={24} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Search Options</TooltipContent>
          </Tooltip>
        </PopoverTrigger>
        <PopoverContent collisionPadding={32} className="rc-z-[1500] rc-w-fit">
          <div className="rc-flex rc-flex-col rc-w-full rc-h-full rc-flex-shrink-0">
            <div className="rc-flex rc-flex-row rc-justify-between rc-items-center rc-mb-2">
              <small className="rc-mr-2">Match Case</small>
              <Switch
                checked={searchData.caseSensitive}
                onCheckedChange={toggleCaseSensitive}
              />
            </div>

            {type === 'PDF' && (
              <div className="rc-flex rc-flex-row rc-justify-between rc-items-center rc-mb-2">
                <small className="rc-mr-2">Highlight All</small>
                <Switch
                  checked={searchData.highlightAll}
                  onCheckedChange={toggleHighlightAll}
                />
              </div>
            )}

            <div className="rc-flex rc-flex-row rc-justify-between rc-items-center rc-mb-2">
              <small className="rc-mr-2">Search for Phrase</small>
              <Switch
                checked={searchData.phraseSearch}
                onCheckedChange={togglePhraseSearch}
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <div className="rc-px-1" />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            onClick={() => {
              findController && findController.reset();
              dispatch(searchActions.setCloseAndClear());
            }}
            variant="light"
            icon
          >
            <MdClose size={24} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Close Search Bar</TooltipContent>
      </Tooltip>
    </div>
  );
};
